import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LuX as X } from 'react-icons/lu';

const Loader = () => (
  <div className="absolute inset-0 flex flex-col items-center justify-center bg-neutral-900 z-20">
    <div className="relative">
      {/* Outer rotating ring */}
      <motion.div
        className="w-16 h-16 border-2 border-white/20 border-t-white rounded-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
      />
      {/* Inner pulsing dot */}
      <motion.div
        className="absolute top-1/2 left-1/2 w-2 h-2 bg-white rounded-full"
        style={{ x: '-50%', y: '-50%' }}
        animate={{
          scale: [1, 1.5, 1],
          opacity: [0.5, 1, 0.5]
        }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
      />
    </div>
    <motion.p
      className="mt-6 text-sm text-neutral-400 font-medium tracking-widest uppercase"
      animate={{ opacity: [0.5, 1, 0.5] }}
      transition={{ duration: 2, repeat: Infinity }}
    >
      Loading Scheduler
    </motion.p>
  </div>
);

const TrafftModal = ({ isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);

      // Preload the script immediately when modal opens
      const script = document.createElement('script');
      script.src = 'https://schedule.mastry.in/embed.js';
      script.async = true;
      
      // Add loading event listener
      script.onload = () => {
        // Give a brief moment for the iframe to render
        setTimeout(() => {
          setIsLoading(false);
        }, 800);
      };

      // Fallback in case script doesn't trigger onload
      const fallbackTimer = setTimeout(() => {
        setIsLoading(false);
      }, 1500);

      document.body.appendChild(script);

      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
        clearTimeout(fallbackTimer);
      };
    }
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/90 z-50 backdrop-blur-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="bg-neutral-900 rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] overflow-hidden relative border border-white/10 flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/10 bg-neutral-900/50 backdrop-blur-sm z-30 absolute top-0 left-0 right-0">
                <div>
                  <h3 className="text-white font-bold text-lg">Book Your Strategy Call</h3>
                  <p className="text-neutral-400 text-sm">30 Minutes • Free Consultation</p>
                </div>
                <button
                  className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors border border-white/10 group"
                  onClick={onClose}
                >
                  <X className="w-5 h-5 text-neutral-400 group-hover:text-white transition-colors" />
                </button>
              </div>

              {/* Content Container */}
              <div className="flex-1 relative mt-[80px] bg-neutral-900">
                <AnimatePresence>
                  {isLoading && (
                    <motion.div
                      initial={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      className="absolute inset-0 z-20 bg-neutral-900"
                    >
                      <Loader />
                    </motion.div>
                  )}
                </AnimatePresence>

                <div
                  className="embedded-booking h-full w-full overflow-y-auto custom-scrollbar"
                  data-url="https://schedule.mastry.in"
                  data-query="&t=s&uuid=1d55b8f4-7e15-481b-a65d-9dd467331af0"
                  data-lang="en"
                  data-autoresize="0"
                  data-showsidebar="1"
                  data-showservices="0"
                  style={{ minWidth: '320px', height: '100%' }}
                />
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default TrafftModal;